import { useState } from "react";
import { hotRanking, latestUpdates, updateFilters } from "@/mocks/home";
import Reveal from "@/components/base/Reveal";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-500",
  down: "ri-arrow-down-line text-secondary-500",
  same: "ri-subtract-line text-foreground-400",
};

export default function LatestUpdatesSection() {
  const [active, setActive] = useState("全部");
  const items =
    active === "全部"
      ? latestUpdates
      : latestUpdates.filter((item) => item.tag === active);

  return (
    <section id="latest" className="border-y border-background-200 bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-8 md:py-14">
        <Reveal>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs text-primary-600">实时聚合 · 两小时内同步</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                最新进展
              </h2>
            </div>
            <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {updateFilters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActive(filter)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
                    active === filter
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-background-300 py-16 text-center text-sm text-foreground-500">
                该分类暂无更新，试试其他标签。
              </p>
            ) : (
              <div className="border-t border-background-200">
                {items.map((item, index) => (
                  <Reveal key={item.id} delay={Math.min(index, 5) * 60}>
                    <a
                      href="#latest"
                      className="group flex items-start gap-4 border-b border-background-200 py-5"
                    >
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-background-50 px-2 py-0.5 text-[11px] text-foreground-700 ring-1 ring-background-200">
                            {item.tag}
                          </span>
                          {item.isNew && (
                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                              新
                            </span>
                          )}
                          <span className="text-[11px] text-foreground-500">
                            {item.source} · {item.time}
                          </span>
                        </div>
                        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                          {item.summary}
                        </p>
                      </div>
                      <span className="hidden shrink-0 flex-col items-end gap-1 pt-1 text-xs text-foreground-500 sm:flex">
                        <i className="ri-fire-line text-base text-primary-500" />
                        {item.heat}
                      </span>
                    </a>
                  </Reveal>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200 bg-background-50">
                <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
                  <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-bar-chart-2-line text-accent-600" />
                    实时热度榜
                  </h3>
                  <span className="text-[11px] text-foreground-500">每 10 分钟刷新</span>
                </div>
                <ol className="px-5 py-2">
                  {hotRanking.map((item) => (
                    <li key={item.rank}>
                      <a
                        href="#latest"
                        className="group flex items-center gap-3 border-b border-background-100 py-3 last:border-none"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            item.rank <= 3
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-100 text-foreground-600"
                          }`}
                        >
                          {item.rank}
                        </span>
                        <span className="flex-1 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="flex shrink-0 items-center gap-1 text-[11px] text-foreground-500">
                          {item.heat}
                          <i className={trendIcon[item.trend]} />
                        </span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h4 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-notification-3-line text-accent-600" />
                  不想错过后续瓜？
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  订阅关注的事件，有新进展时第一时间推送提醒，附来源与核实状态。
                </p>
                <a
                  href="#topics"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-add-line" />
                  订阅追踪
                </a>
              </div>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}